import { type CreatePatient, type Patient, RED_FLAGS, SEX_AT_BIRTH } from '@dnx/contracts';
import { formatCpf } from '@dnx/domain';
import { Alert, Badge, Button, Card, Dialog, EmptyState, Field, Input, PageHeader, Select, Spinner } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ClipboardPlus, Search, ShieldCheck, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { day, patientsQuery, SEX_LABELS } from './queries.js';

export function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError)) return {};
  const list = (err.problem as { errors?: { path: string; message: string }[] } | undefined)?.errors ?? [];
  return Object.fromEntries(list.map((e) => [e.path, e.message]));
}

function NewPatientDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: '', phone: '', cpf: '', birthDate: '', sexAtBirth: '' });
  const create = useMutation({
    mutationFn: () => api<Patient>('/patients', {
      method: 'POST',
      body: {
        name: f.name, phone: f.phone, ...(f.cpf ? { cpf: f.cpf } : {}), ...(f.birthDate ? { birthDate: f.birthDate } : {}),
        ...(f.sexAtBirth ? { sexAtBirth: f.sexAtBirth as CreatePatient['sexAtBirth'] } : {}),
      } satisfies Partial<CreatePatient>,
    }),
    onSuccess: (p) => { void qc.invalidateQueries({ queryKey: ['patients'] }); onClose(); void navigate({ to: '/pacientes/$id', params: { id: p.id } }); },
  });
  const errs = fieldErrors(create.error);
  return (
    <Dialog open={open} onClose={onClose} title="Abrir prontuário" description="Se o celular já for de um contato (ex.: veio pelo WhatsApp), o prontuário é aberto nele.">
      <form className="grid gap-4 p-5" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <Field label="Nome completo" error={errs.name}><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus required /></Field>
        <Field label="Celular (com DDD)" error={errs.phone}><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} inputMode="tel" required /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="CPF" hint="Opcional agora; exigido no orçamento." error={errs.cpf}>
            <Input value={f.cpf} onChange={(e) => setF({ ...f, cpf: formatCpf(e.target.value) })} inputMode="numeric" placeholder="000.000.000-00" />
          </Field>
          <Field label="Nascimento" error={errs.birthDate}><Input type="date" value={f.birthDate} onChange={(e) => setF({ ...f, birthDate: e.target.value })} /></Field>
        </div>
        <Field label="Sexo biológico">
          <Select value={f.sexAtBirth} onChange={(e) => setF({ ...f, sexAtBirth: e.target.value })}>
            <option value="">—</option>
            {SEX_AT_BIRTH.map((s) => <option key={s} value={s}>{SEX_LABELS[s]}</option>)}
          </Select>
        </Field>
        {create.error && !Object.keys(errs).length && <Alert tone="danger">{create.error.message}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button type="submit" loading={create.isPending}>Abrir prontuário</Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Pacientes: prontuários da clínica. Só a equipe clínica vê; cada abertura fica registrada. */
export function PatientsPage() {
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  useEffect(() => { const t = setTimeout(() => setQ(term.trim()), 300); return () => clearTimeout(t); }, [term]);
  const { data, isLoading } = useQuery(patientsQuery(q));
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Pacientes" description="Prontuário com anamnese, avaliação capilar e histórico. Dados de saúde ficam cifrados e cada acesso é registrado."
        actions={<Button onClick={() => setCreating(true)}><ClipboardPlus className="size-4" />Novo paciente</Button>} />
      <div className="relative mt-7 max-w-md">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-subtle" aria-hidden />
        <Input aria-label="Buscar paciente" className="pl-9" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Nome, código PAC ou CPF completo" />
      </div>
      {isLoading ? <Spinner className="mt-10 size-5 text-fg-subtle" label="Carregando" /> : data?.length ? (
        <Card className="mt-5 divide-y divide-border overflow-hidden">
          {data.map((p) => (
            <Link key={p.id} to="/pacientes/$id" params={{ id: p.id }} className="grid gap-2 px-5 py-4 transition-colors hover:bg-surface-muted sm:grid-cols-[minmax(0,1fr)_220px_180px] sm:items-center">
              <div className="min-w-0">
                <p className="truncate font-semibold text-fg">{p.name}</p>
                <p className="mt-0.5 text-[13px] text-fg-muted">
                  <span className="font-mono text-[12px]">{p.pseudonym}</span>
                  {p.age != null && ` · ${p.age} anos`}{p.phoneLast4 && ` · final ${p.phoneLast4}`}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {p.redFlags.length ? (
                  <Badge tone="warning"><TriangleAlert className="size-3" />{p.redFlags.length === 1 ? RED_FLAGS[p.redFlags[0] as keyof typeof RED_FLAGS] ?? '1 alerta' : `${p.redFlags.length} alertas`}</Badge>
                ) : null}
              </div>
              <div className="text-[13px] text-fg-muted sm:text-right">
                {p.lastEvaluation ? (
                  <span className="inline-flex items-center gap-1.5">
                    {p.lastEvaluation.status === 'signed' ? <ShieldCheck className="size-3.5 text-success" /> : null}
                    {p.lastEvaluation.classification ?? 'Avaliação'} · {day(p.lastEvaluation.at)}
                  </span>
                ) : 'Sem avaliação'}
              </div>
            </Link>
          ))}
        </Card>
      ) : q ? (
        <p className="mt-8 text-sm text-fg-muted">Nenhum paciente encontrado para “{q}”. CPF só encontra com os 11 dígitos.</p>
      ) : (
        <EmptyState className="mt-8" icon={<ClipboardPlus className="size-6" />} title="Nenhum prontuário ainda"
          actions={<Button onClick={() => setCreating(true)}>Abrir o primeiro</Button>}>
          <p>Abra o prontuário na chegada para a avaliação: cadastro, anamnese com sinais de alerta e a avaliação capilar do médico.</p>
        </EmptyState>
      )}
      {creating && <NewPatientDialog open onClose={() => setCreating(false)} />}
    </div>
  );
}
